import { useState } from 'react';
import { FiClock, FiChevronDown, FiChevronUp, FiEdit2, FiTrash2, FiPackage, FiAlertTriangle } from 'react-icons/fi';

const RecipeCard = ({ recipe, onEdit, onDelete, currency }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Safely handle undefined/null values
  const preparationTime = recipe.preparationTime || recipe.preparation_time || 0;
  const cost = recipe.cost || 0;
  const convertedCost = (cost * currency.exchangeRate).toFixed(2);
  const expiryDuration = recipe.expiryDuration || recipe.expiry_duration;
  const ingredients = recipe.ingredients || [];
  const warnings = recipe.warnings || {};

  // Group warnings by type
  const lowStockWarnings = Object.entries(warnings)
    .filter(([_, message]) => message.includes('Low stock') || message.includes('Only'));
  const expiryWarnings = Object.entries(warnings)
    .filter(([_, message]) => message.includes('expire') || message.includes('Expir'));

  return (
    <div className="w-full">
      <div className={`bg-white p-3 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow h-full 
        ${lowStockWarnings.length > 0 ? 'border-l-4 border-l-yellow-500' : ''}
        ${expiryWarnings.length > 0 ? 'border-l-4 border-l-red-500' : ''}`}>
        
        {(lowStockWarnings.length > 0 || expiryWarnings.length > 0) && (
          <div className="flex flex-wrap gap-1 mb-2">
            {lowStockWarnings.map(([ingredient, message]) => (
              <div key={`low-${ingredient}`} className="flex items-center bg-yellow-50 text-yellow-800 px-2 py-1 rounded text-xs">
                <FiAlertTriangle className="mr-1" size={12} />
                {message}
              </div>
            ))}
            {expiryWarnings.map(([ingredient, message]) => (
              <div key={`exp-${ingredient}`} className="flex items-center bg-red-50 text-red-800 px-2 py-1 rounded text-xs">
                <FiAlertTriangle className="mr-1" size={12} />
                {message}
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3">
          <div className="flex-shrink-0 w-20 h-20">
            {recipe.image_url ? (
              <img 
                src={`${process.env.REACT_APP_API_URL}/uploads/${recipe.image_url}`}
                alt={recipe.name}
                className="h-full w-full object-cover rounded-md border border-gray-200"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.parentElement.innerHTML = (
                    <div className="h-full w-full flex items-center justify-center bg-gray-100 rounded-md border border-gray-200">
                      <FiPackage size={16} className="text-gray-400" />
                    </div>
                  );
                }}
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-gray-100 rounded-md border border-gray-200">
                <FiPackage size={24} className="text-gray-400" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <h3 className="font-medium text-gray-800 truncate text-sm">{recipe.name}</h3>
                <p className="text-gray-500 text-xs mt-1 line-clamp-2">{recipe.description}</p>
              </div>
              <div className="flex gap-1 items-center flex-shrink-0">
                <button 
                  onClick={onEdit}
                  className="text-gray-500 hover:text-blue-600 p-1 rounded-md transition-colors"
                  aria-label="Edit"
                  title="Edit recipe"
                >
                  <FiEdit2 size={16} />
                </button>
                <button 
                  onClick={onDelete}
                  className="text-gray-500 hover:text-red-600 p-1 rounded-md transition-colors"
                  aria-label="Delete"
                  title="Delete recipe"
                >
                  <FiTrash2 size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-md transition-colors"
                  aria-label={isExpanded ? "Collapse" : "Expand"}
                >
                  {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                </button>
              </div>
            </div>

            <div className="mt-2 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-gray-600">
                  <FiClock className="text-gray-400" size={12} />
                  <span>{preparationTime} mins</span>
                </div>
                {expiryDuration && (
                  <div className="flex items-center gap-1 text-gray-600">
                    <FiClock className="text-gray-400" size={12} />
                    <span>Expires in {expiryDuration} days</span>
                  </div>
                )}
              </div>
              <div className="font-medium text-blue-600">
                {currency.symbol}{cost}
              </div>
            </div>

            {isExpanded && (
              <div className="mt-3 space-y-3 text-xs">
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">Instructions</h4>
                  <p className="text-gray-600 whitespace-pre-line">{recipe.instructions}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-1">Ingredients</h4>
                  <ul className="space-y-1">
                    {ingredients.map((ingredient, index) => {
                      const ingredientWarnings = warnings[ingredient.name] ? 
                        <span className="text-red-500 ml-1">
                          <FiAlertTriangle className="inline mr-1" size={12} />
                          {warnings[ingredient.name]}
                        </span> : null;
                      
                      return (
                        <li key={index} className="flex justify-between">
                          <span className="text-gray-800">
                            {ingredient.name}
                            {ingredientWarnings}
                          </span>
                          <span className="text-gray-600">
                            {ingredient.quantity} {ingredient.unit}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeCard;